import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, { siteConfig } from "../Datas/productData";
import "../css/checkout.css";

const formatMoney = (value) =>
  new Intl.NumberFormat(siteConfig.currency.locale, {
    style: "currency",
    currency: siteConfig.currency.code,
  }).format(Number(value) || 0);

const initialForm = {
  email: "",
  phone: "",
  firstName: "",
  lastName: "",
  company: "",
  country: "DE",
  address: "",
  apartment: "",
  city: "",
  postalCode: "",
  shippingMethod: "standard",
  acceptsTerms: false,
};

const validateCheckout = (values) => {
  const nextErrors = {};
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!values.email.trim()) {
    nextErrors.email = "Enter your email address.";
  } else if (!emailPattern.test(values.email.trim())) {
    nextErrors.email = "Enter a valid email address.";
  }
  if (values.phone && values.phone.replace(/\D/g, "").length < 7) {
    nextErrors.phone = "Enter a valid phone number or leave this field empty.";
  }
  if (!values.firstName.trim()) nextErrors.firstName = "Enter your first name.";
  if (!values.lastName.trim()) nextErrors.lastName = "Enter your last name.";
  if (!values.country) nextErrors.country = "Select a destination country.";
  if (!values.address.trim()) nextErrors.address = "Enter a street address.";
  if (!values.city.trim()) nextErrors.city = "Enter a city.";
  if (!values.postalCode.trim()) {
    nextErrors.postalCode = "Enter a postal code.";
  } else if (values.postalCode.trim().length < 3) {
    nextErrors.postalCode = "Enter a valid postal code.";
  }
  if (!values.shippingMethod) {
    nextErrors.shippingMethod = "Choose a delivery method.";
  }
  if (!values.acceptsTerms) {
    nextErrors.acceptsTerms = "Please accept the terms to place your order.";
  }

  return nextErrors;
};

const CheckOut = () => {
  const navigate = useNavigate();
  const { cartItems, cartSubtotal, clearCart } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [failedImages, setFailedImages] = useState({});

  const orderItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const derivedSubtotal = orderItems.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const subtotal = Number.isFinite(cartSubtotal)
    ? cartSubtotal
    : derivedSubtotal;
  const qualifiesForFreeShipping =
    subtotal >= Number(siteConfig.shipping.freeThreshold);
  const shippingCost =
    form.shippingMethod === "express"
      ? Number(siteConfig.shipping.expressFee)
      : qualifiesForFreeShipping
      ? 0
      : Number(siteConfig.shipping.deliveryFee);
  const total = subtotal + shippingCost;

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const markImageAsFailed = (source) => {
    setFailedImages((current) => ({ ...current, [source]: true }));
  };

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    const nextValue = type === "checkbox" ? checked : value;
    setForm((current) => ({ ...current, [name]: nextValue }));

    if (errors[name]) {
      setErrors((current) => {
        const nextErrors = { ...current };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    const fieldErrors = validateCheckout(form);
    setErrors((current) => {
      const nextErrors = { ...current };
      if (fieldErrors[name]) {
        nextErrors[name] = fieldErrors[name];
      } else {
        delete nextErrors[name];
      }
      return nextErrors;
    });
  };

  const handlePlaceOrder = (event) => {
    event.preventDefault();
    const nextErrors = validateCheckout(form);
    setErrors(nextErrors);
    setHasSubmitted(true);

    if (Object.keys(nextErrors).length > 0 || orderItems.length === 0) {
      const firstError = Object.keys(nextErrors)[0];
      if (firstError) {
        window.requestAnimationFrame(() => {
          document.getElementById(`checkout-${firstError}`)?.focus();
        });
      }
      return;
    }

    const orderNumber = `MC-${Date.now().toString().slice(-8)}`;
    clearCart();
    navigate("/paysuccess", {
      replace: true,
      state: {
        orderNumber,
        total,
        email: form.email,
        deliveryMethod: form.shippingMethod,
      },
    });
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const fieldError = (name) =>
    errors[name] ? (
      <p className="checkout-field__error" id={`checkout-${name}-error`} role="alert">
        {errors[name]}
      </p>
    ) : null;

  if (!orderItems.length) {
    return (
      <main className="checkout-page checkout-page--empty">
        <p>Checkout</p>
        <h1>Your bag is empty.</h1>
        <span>Add a piece from the collection before continuing to checkout.</span>
        <Link to="/shop" onClick={toTop}>
          Explore the collection
        </Link>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <header className="checkout-page__header">
        <Link to="/cartpage" onClick={toTop}>
          <span aria-hidden="true">←</span> Return to bag
        </Link>
        <div>
          <p>{siteConfig.brandName}</p>
          <h1>Checkout</h1>
        </div>
        <span>Secure demo</span>
      </header>

      <div className="checkout-page__layout">
        <form
          className="checkout-form"
          onSubmit={handlePlaceOrder}
          noValidate
          aria-label="Checkout information"
        >
          {hasSubmitted && Object.keys(errors).length > 0 && (
            <div className="checkout-form__alert" role="alert">
              Please review the highlighted fields before placing your order.
            </div>
          )}

          <section className="checkout-section" aria-labelledby="contact-heading">
            <div className="checkout-section__heading">
              <span>01</span>
              <div>
                <p>Stay in touch</p>
                <h2 id="contact-heading">Contact</h2>
              </div>
            </div>

            <div className="checkout-fields">
              <label className="checkout-field checkout-field--wide">
                <span>Email address *</span>
                <input
                  id="checkout-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="email"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "checkout-email-error" : undefined}
                />
                {fieldError("email")}
              </label>

              <label className="checkout-field checkout-field--wide">
                <span>Phone number <small>Optional</small></span>
                <input
                  id="checkout-phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="tel"
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? "checkout-phone-error" : undefined}
                />
                {fieldError("phone")}
              </label>
            </div>
          </section>

          <section className="checkout-section" aria-labelledby="delivery-heading">
            <div className="checkout-section__heading">
              <span>02</span>
              <div>
                <p>Where it is going</p>
                <h2 id="delivery-heading">Delivery</h2>
              </div>
            </div>

            <div className="checkout-fields">
              <label className="checkout-field">
                <span>First name *</span>
                <input
                  id="checkout-firstName"
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="given-name"
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={errors.firstName ? "checkout-firstName-error" : undefined}
                />
                {fieldError("firstName")}
              </label>

              <label className="checkout-field">
                <span>Last name *</span>
                <input
                  id="checkout-lastName"
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="family-name"
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={errors.lastName ? "checkout-lastName-error" : undefined}
                />
                {fieldError("lastName")}
              </label>

              <label className="checkout-field checkout-field--wide">
                <span>Company <small>Optional</small></span>
                <input
                  id="checkout-company"
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                  autoComplete="organization"
                />
              </label>

              <label className="checkout-field checkout-field--wide">
                <span>Country / region *</span>
                <select
                  id="checkout-country"
                  name="country"
                  value={form.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="country"
                  aria-invalid={Boolean(errors.country)}
                  aria-describedby={errors.country ? "checkout-country-error" : undefined}
                >
                  <option value="DE">Germany</option>
                  <option value="AT">Austria</option>
                  <option value="BE">Belgium</option>
                  <option value="FR">France</option>
                  <option value="IT">Italy</option>
                  <option value="NL">Netherlands</option>
                  <option value="ES">Spain</option>
                  <option value="GB">United Kingdom</option>
                  <option value="US">United States</option>
                </select>
                {fieldError("country")}
              </label>

              <label className="checkout-field checkout-field--wide">
                <span>Street address *</span>
                <input
                  id="checkout-address"
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="address-line1"
                  aria-invalid={Boolean(errors.address)}
                  aria-describedby={errors.address ? "checkout-address-error" : undefined}
                />
                {fieldError("address")}
              </label>

              <label className="checkout-field checkout-field--wide">
                <span>Apartment, suite, etc. <small>Optional</small></span>
                <input
                  id="checkout-apartment"
                  type="text"
                  name="apartment"
                  value={form.apartment}
                  onChange={handleChange}
                  autoComplete="address-line2"
                />
              </label>

              <label className="checkout-field">
                <span>City *</span>
                <input
                  id="checkout-city"
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="address-level2"
                  aria-invalid={Boolean(errors.city)}
                  aria-describedby={errors.city ? "checkout-city-error" : undefined}
                />
                {fieldError("city")}
              </label>

              <label className="checkout-field">
                <span>Postal code *</span>
                <input
                  id="checkout-postalCode"
                  type="text"
                  name="postalCode"
                  value={form.postalCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="postal-code"
                  aria-invalid={Boolean(errors.postalCode)}
                  aria-describedby={errors.postalCode ? "checkout-postalCode-error" : undefined}
                />
                {fieldError("postalCode")}
              </label>
            </div>
          </section>

          <section className="checkout-section" aria-labelledby="shipping-heading">
            <div className="checkout-section__heading">
              <span>03</span>
              <div>
                <p>How it arrives</p>
                <h2 id="shipping-heading">Delivery method</h2>
              </div>
            </div>

            <div className="checkout-methods">
              <label className={form.shippingMethod === "standard" ? "is-selected" : ""}>
                <input
                  id="checkout-shippingMethod"
                  type="radio"
                  name="shippingMethod"
                  value="standard"
                  checked={form.shippingMethod === "standard"}
                  onChange={handleChange}
                />
                <span>
                  <strong>Standard delivery</strong>
                  <small>{siteConfig.shipping.deliveryWindow}</small>
                </span>
                <b>
                  {qualifiesForFreeShipping
                    ? "Complimentary"
                    : formatMoney(siteConfig.shipping.deliveryFee)}
                </b>
              </label>
              <label className={form.shippingMethod === "express" ? "is-selected" : ""}>
                <input
                  type="radio"
                  name="shippingMethod"
                  value="express"
                  checked={form.shippingMethod === "express"}
                  onChange={handleChange}
                />
                <span>
                  <strong>Express delivery</strong>
                  <small>1–2 business days</small>
                </span>
                <b>{formatMoney(siteConfig.shipping.expressFee)}</b>
              </label>
              {fieldError("shippingMethod")}
            </div>
          </section>

          <section className="checkout-section" aria-labelledby="payment-heading">
            <div className="checkout-section__heading">
              <span>04</span>
              <div>
                <p>Front-end demonstration</p>
                <h2 id="payment-heading">Payment</h2>
              </div>
            </div>

            <div className="checkout-demo-payment">
              <span aria-hidden="true">◎</span>
              <div>
                <strong>Demo payment — no card details required</strong>
                <p>
                  This storefront demonstration never asks for, transmits or stores
                  real payment information. Placing the order will not charge you.
                </p>
              </div>
            </div>

            <label className="checkout-terms">
              <input
                id="checkout-acceptsTerms"
                type="checkbox"
                name="acceptsTerms"
                checked={form.acceptsTerms}
                onChange={handleChange}
                aria-invalid={Boolean(errors.acceptsTerms)}
                aria-describedby={
                  errors.acceptsTerms ? "checkout-acceptsTerms-error" : undefined
                }
              />
              <span>
                I agree to the <Link to="/terms">terms</Link> and acknowledge the{" "}
                <Link to="/privacy">privacy policy</Link>.
              </span>
            </label>
            {fieldError("acceptsTerms")}
          </section>

          <button className="checkout-form__submit" type="submit">
            Place demo order · {formatMoney(total)}
          </button>
          <p className="checkout-form__legal">
            Need help? <Link to="/contact">Contact the atelier</Link> or read our{" "}
            <Link to="/returns">returns policy</Link>.
          </p>
        </form>

        <aside className="checkout-summary" aria-label="Order summary">
          <div className="checkout-summary__heading">
            <p>Your selection</p>
            <h2>Order summary</h2>
            <span>
              {orderItems.reduce((count, item) => count + item.quantity, 0)} items
            </span>
          </div>

          <div className="checkout-summary__items">
            {orderItems.map((item) => {
              const image = item.product.image || item.product.images?.[0];
              return (
                <article key={`${item.id}-${item.size}-${item.color}`}>
                  <div className="checkout-summary__media">
                    {image && !failedImages[image] ? (
                      <img
                        src={image}
                        alt={item.product.name}
                        onError={() => markImageAsFailed(image)}
                      />
                    ) : (
                      <span>{siteConfig.brandName}</span>
                    )}
                    <small>{item.quantity}</small>
                  </div>
                  <div>
                    <p>{item.product.name}</p>
                    <span>
                      {item.color || "Standard"} / {item.size || "One size"}
                    </span>
                  </div>
                  <strong>
                    {formatMoney(Number(item.product.price) * item.quantity)}
                  </strong>
                </article>
              );
            })}
          </div>

          <dl className="checkout-summary__totals">
            <div>
              <dt>Subtotal</dt>
              <dd>{formatMoney(subtotal)}</dd>
            </div>
            <div>
              <dt>Delivery</dt>
              <dd>{shippingCost === 0 ? "Complimentary" : formatMoney(shippingCost)}</dd>
            </div>
            <div className="checkout-summary__total">
              <dt>Total</dt>
              <dd>
                <small>{siteConfig.currency.code}</small> {formatMoney(total)}
              </dd>
            </div>
          </dl>
          <p className="checkout-summary__tax">Tax included where applicable.</p>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
